import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './Points.css';

function Points() {
  const [points, setPoints] = useState(null);
  const [redeemConfig, setRedeemConfig] = useState(null);
  const [earningRules, setEarningRules] = useState([]);
  const [activities, setActivities] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [buyingId, setBuyingId] = useState(null);
  const [productMessage, setProductMessage] = useState('');
  const [productMessageType, setProductMessageType] = useState('');
  const [redeemPoints, setRedeemPoints] = useState('');
  const [paytmMobile, setPaytmMobile] = useState('');
  const [confirmPaytmMobile, setConfirmPaytmMobile] = useState('');
  const [savePaytmMobile, setSavePaytmMobile] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pointsRes, configRes, rulesRes, activitiesRes, redemptionsRes, productsRes, purchasesRes] = await Promise.all([
        axios.get('/api/points/me'),
        axios.get('/api/points/redeem/config'),
        axios.get('/api/points/earning-rules'),
        axios.get('/api/points/activities?page=0&size=20'),
        axios.get('/api/points/redemptions?page=0&size=10'),
        axios.get('/api/points/products'),
        axios.get('/api/points/purchases?page=0&size=5')
      ]);
      setPoints(pointsRes.data);
      setRedeemConfig(configRes.data);
      setEarningRules(rulesRes.data);
      setActivities(activitiesRes.data.content || []);
      setRedemptions(redemptionsRes.data.content || []);
      setProducts(productsRes.data || []);
      setPurchases(purchasesRes.data.content || []);
    } catch (error) {
      console.error('Error fetching points:', error);
    } finally {
      setLoading(false);
    }
  };

  const pointsPerRupee = redeemConfig?.pointsPerRupee || 10;
  const minPoints = redeemConfig?.minPoints || 100;

  const cashPreview = useMemo(() => {
    const amount = parseInt(redeemPoints, 10);
    if (!amount || amount <= 0) return 0;
    return amount / pointsPerRupee;
  }, [redeemPoints, pointsPerRupee]);

  const availableCash = useMemo(() => {
    return (points?.availablePoints || 0) / pointsPerRupee;
  }, [points, pointsPerRupee]);

  const showMessage = (text, type = 'error') => {
    setMessage(text);
    setMessageType(type);
  };

  const handleRedeemToPaytm = async (e) => {
    e.preventDefault();
    const amount = parseInt(redeemPoints, 10);

    if (!amount || amount <= 0) {
      showMessage('Please enter a valid number of points');
      return;
    }

    if (amount < minPoints) {
      showMessage(`Minimum redemption is ${minPoints} points (₹${minPoints / pointsPerRupee})`);
      return;
    }

    if (amount % pointsPerRupee !== 0) {
      showMessage(`Points must be in multiples of ${pointsPerRupee} (₹1 = ${pointsPerRupee} points)`);
      return;
    }

    if (amount > (points?.availablePoints || 0)) {
      showMessage('Insufficient points');
      return;
    }

    if (!/^[6-9]\d{9}$/.test(paytmMobile.trim())) {
      showMessage('Enter a valid 10-digit Paytm mobile number');
      return;
    }

    if (paytmMobile.trim() !== confirmPaytmMobile.trim()) {
      showMessage('Paytm mobile numbers do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post('/api/points/redeem/paytm', {
        points: amount,
        paytmMobile: paytmMobile.trim(),
        confirmPaytmMobile: confirmPaytmMobile.trim(),
        savePaytmMobile
      });

      const result = response.data;
      if (result.status === 'SUCCESS') {
        showMessage(
          `₹${result.cashAmountInr} sent to Paytm ${result.paytmMobile}. Transaction ID: ${result.paytmTransactionId}`,
          'success'
        );
        setRedeemPoints('');
        setConfirmPaytmMobile('');
        fetchData();
      } else {
        showMessage(result.failureReason || 'Paytm transfer failed. Points were not deducted.');
      }
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to redeem points to Paytm');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuyProduct = async (product) => {
    if (!product.affordable) {
      setProductMessage(`You need ${product.pointsCost} points. You have ${points?.availablePoints || 0}.`);
      setProductMessageType('error');
      return;
    }

    if (!window.confirm(`Buy "${product.name}" for ${product.pointsCost} points?`)) {
      return;
    }

    setBuyingId(product.id);
    setProductMessage('');
    try {
      const response = await axios.post(`/api/points/products/${product.id}/purchase`);
      const coupon = response.data.couponCode;
      if (coupon) {
        setProductMessage(
          `Purchased ${response.data.productIcon || ''} ${response.data.productName}. Your coupon code: ${coupon}`
        );
      } else {
        setProductMessage(
          `Purchased ${response.data.productIcon || ''} ${response.data.productName}! Check your email for delivery details.`
        );
      }
      setProductMessageType('success');
      fetchData();
    } catch (error) {
      setProductMessage(error.response?.data?.message || 'Purchase failed');
      setProductMessageType('error');
    } finally {
      setBuyingId(null);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleString();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="points-container">
      <div className="points-header">
        <h1>Your Reward Points</h1>
        <p>Earn points on Socify and redeem them as Paytm wallet cash.</p>
      </div>

      <div className="points-cards">
        <div className="points-card total">
          <div className="card-icon">🏆</div>
          <div className="card-content">
            <h3>Total Points Earned</h3>
            <div className="points-value">{points?.totalPoints || 0}</div>
          </div>
        </div>

        <div className="points-card available">
          <div className="card-icon">💎</div>
          <div className="card-content">
            <h3>Available Points</h3>
            <div className="points-value">{points?.availablePoints || 0}</div>
            <div className="cash-value">≈ ₹{availableCash.toFixed(2)} Paytm cash</div>
          </div>
        </div>

        <div className="points-card redeemed">
          <div className="card-icon">✨</div>
          <div className="card-content">
            <h3>Redeemed Points</h3>
            <div className="points-value">{points?.redeemedPoints || 0}</div>
          </div>
        </div>
      </div>

      <div className="redeem-section paytm-redeem">
        <div className="paytm-header">
          <div className="paytm-logo">Paytm</div>
          <div>
            <h2>Redeem to Paytm Wallet</h2>
            <p>
              {pointsPerRupee} points = ₹1 · Minimum {minPoints} points (₹{minPoints / pointsPerRupee})
              {redeemConfig?.savedPaytmMobile && (
                <> · Saved number: {redeemConfig.savedPaytmMobile}</>
              )}
            </p>
          </div>
        </div>

        {message && (
          <div className={`message ${messageType === 'success' ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <form onSubmit={handleRedeemToPaytm} className="paytm-redeem-form">
          <div className="form-row">
            <label htmlFor="redeemPoints">Points to redeem</label>
            <input
              id="redeemPoints"
              type="number"
              value={redeemPoints}
              onChange={(e) => setRedeemPoints(e.target.value)}
              placeholder={`Min ${minPoints} points`}
              min={minPoints}
              max={points?.availablePoints || 0}
              step={pointsPerRupee}
            />
            <div className="cash-preview">
              You will receive <strong>₹{cashPreview.toFixed(2)}</strong> in your Paytm wallet
            </div>
          </div>

          <div className="form-row two-col">
            <div>
              <label htmlFor="paytmMobile">Paytm mobile number</label>
              <input
                id="paytmMobile"
                type="tel"
                value={paytmMobile}
                onChange={(e) => setPaytmMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit mobile number"
                maxLength={10}
              />
            </div>
            <div>
              <label htmlFor="confirmPaytmMobile">Confirm mobile number</label>
              <input
                id="confirmPaytmMobile"
                type="tel"
                value={confirmPaytmMobile}
                onChange={(e) => setConfirmPaytmMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Re-enter mobile number"
                maxLength={10}
              />
            </div>
          </div>

          <label className="save-mobile">
            <input
              type="checkbox"
              checked={savePaytmMobile}
              onChange={(e) => setSavePaytmMobile(e.target.checked)}
            />
            Save Paytm number for future redemptions
          </label>

          <button type="submit" className="redeem-btn paytm-btn" disabled={submitting}>
            {submitting ? 'Transferring to Paytm...' : 'Transfer to Paytm Wallet'}
          </button>
        </form>
      </div>

      {redemptions.length > 0 && (
        <div className="redemption-history">
          <h2>Paytm Redemption History</h2>
          <div className="redemption-list">
            {redemptions.map(item => (
              <div key={item.id} className={`redemption-item status-${item.status.toLowerCase()}`}>
                <div>
                  <div className="redemption-amount">₹{item.cashAmountInr} → {item.paytmMobile}</div>
                  <div className="redemption-meta">
                    {item.pointsRedeemed} pts · {formatDate(item.createdAt)}
                    {item.paytmTransactionId && <> · Txn {item.paytmTransactionId}</>}
                  </div>
                </div>
                <span className="redemption-status">{item.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="product-store">
        <h2>Rewards Store</h2>
        <p className="store-subtitle">Spend your points on gift cards, learning resources, and merch.</p>

        {productMessage && (
          <div className={`message ${productMessageType === 'success' ? 'success' : 'error'}`}>
            {productMessage}
          </div>
        )}

        <div className="product-grid">
          {products.map(product => (
            <div key={product.id} className={`product-card ${product.affordable ? '' : 'locked'}`}>
              <div className="product-icon">{product.icon}</div>
              <span className="product-category">{product.category}</span>
              <h3>{product.name}</h3>
              <p>{product.description}</p>
              <div className="product-footer">
                <span className="product-price">{product.pointsCost} pts</span>
                <button
                  type="button"
                  className="buy-btn"
                  disabled={!product.affordable || buyingId === product.id}
                  onClick={() => handleBuyProduct(product)}
                >
                  {buyingId === product.id ? 'Buying...' : product.affordable ? 'Buy Now' : 'Need more pts'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {purchases.length > 0 && (
          <div className="purchase-history">
            <h3>Your Purchases</h3>
            <div className="purchase-list">
              {purchases.map(item => (
                <div key={item.id} className="purchase-item">
                  <span>
                    {item.productIcon} {item.productName}
                    {item.couponCode ? (
                      <> · code <code>{item.couponCode}</code></>
                    ) : null}
                  </span>
                  <span className="purchase-meta">{item.pointsSpent} pts · {formatDate(item.purchasedAt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="points-earning">
        <h2>How to Earn Points</h2>
        <div className="earning-list">
          {earningRules.map(rule => (
            <div key={rule.activityType} className="earning-item">
              <span className="earning-icon">{rule.icon}</span>
              <span className="earning-action">{rule.label}</span>
              <span className="earning-points">+{rule.points} points</span>
            </div>
          ))}
        </div>
      </div>

      <div className="activity-history">
        <h2>Recent Activity</h2>
        {activities.length === 0 ? (
          <p className="no-activities">No activity yet. Start engaging to earn points!</p>
        ) : (
          <div className="activity-list">
            {activities.map(activity => (
              <div key={activity.id} className="activity-item">
                <div className="activity-info">
                  <span className="activity-label">{activity.activityLabel}</span>
                  {activity.description && (
                    <span className="activity-desc">{activity.description}</span>
                  )}
                  <span className="activity-date">{formatDate(activity.createdAt)}</span>
                </div>
                <span className={`activity-points ${activity.pointsEarned < 0 ? 'negative' : ''}`}>
                  {activity.pointsEarned > 0 ? '+' : ''}{activity.pointsEarned} pts
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Points;
